import { toast } from 'sonner'

import { findSnippetIn } from '@/components/milkdown/snippet/snippetBlock'
import { useMountEffect } from '@/hooks/useMountEffect'
import { useSnippetEditorStore } from '@/stores/snippetEditorStore'
import { SnippetEditorSheet } from './SnippetEditorSheet'
import {
  baseDraft,
  customizeDraftFor,
  draftFor,
  type SnippetDraft,
} from './drafts'
import { useSnippetAccess } from './hooks/useSnippetAccess'
import {
  DELETED_MESSAGE,
  useSnippetEditorSheet,
} from './hooks/useSnippetEditorSheet'
import { useSnippetsQuery } from './hooks/useSnippets'
import { useSnippetVariantsQuery } from './hooks/useSnippetVariants'
import type { Snippet } from './types'
import { canCustomize } from './utils'

function LoadedSheet({
  draft,
  onClosed,
}: {
  draft: SnippetDraft
  onClosed: () => void
}) {
  // Seeded at mount from the loaded snippet. The parent keys this on the
  // snippet id, so switching snippets remounts rather than syncing via effect,
  // and a refetch never overwrites what the user is typing.
  const { sheetProps } = useSnippetEditorSheet(onClosed, draft)
  return <SnippetEditorSheet {...sheetProps} />
}

/**
 * Editing one duty's wording. The body starts from whatever that duty renders
 * today — its own wording if it has one, the shared body if not — so "Create
 * variant" opens on the text the author is looking at rather than a blank box.
 *
 * The variant's `updated_at` comes from the variants list rather than the
 * snippet list, which carries the content but not the row's own timestamp.
 * A customised snippet waits for that list and gives up if it fails, rather
 * than opening without the timestamp: the save would then ship with no
 * precondition, and the client-side fork check would read an existing variant
 * as never forked and swallow it. Waiting also keeps the fetch landing from
 * remounting the editor mid-typing.
 */
function VariantSheet({
  snippet,
  dutyId,
  onClosed,
}: {
  snippet: Snippet
  dutyId: string
  onClosed: () => void
}) {
  const { data, isPending } = useSnippetVariantsQuery(snippet.id)
  const customised = typeof snippet.duty_content === 'string'
  if (customised && !data) {
    if (isPending) return null
    return (
      <CloseWithToast
        message="Could not load this variant."
        onClosed={onClosed}
      />
    )
  }
  const row = data?.variants.find((variant) => variant.duty_id === dutyId)

  const draft: SnippetDraft = {
    ...baseDraft(snippet),
    content: customised ? snippet.duty_content! : snippet.content,
    variant: {
      dutyId,
      sharedContent: snippet.content,
      updatedAt: customised ? (row?.updated_at ?? null) : null,
    },
  }

  return (
    <LoadedSheet
      key={`${snippet.id}:${dutyId}`}
      draft={draft}
      onClosed={onClosed}
    />
  )
}

/** Rendered when there is nothing to edit: report it once and close. */
function CloseWithToast({
  message,
  onClosed,
}: {
  message: string
  onClosed: () => void
}) {
  useMountEffect(() => {
    toast.error(message)
    onClosed()
  })
  return null
}

function OpenEditor({
  snippetId,
  dutyId,
}: {
  snippetId: string
  dutyId: string | null
}) {
  const closeSnippetEditor = useSnippetEditorStore(
    (state) => state.closeSnippetEditor
  )
  const { canShare, managedElsewhere } = useSnippetAccess()
  // Scoped to the same duty the block reads from, so `duty_content` is present
  // and this cache entry is the one the block already warmed.
  const query = useSnippetsQuery(true, dutyId ?? undefined)
  // The block's token holds the shared id; the org's copy answers to it.
  const snippet = query.data
    ? findSnippetIn(query.data.snippets, snippetId)
    : undefined
  if (!snippet) {
    if (query.isPending) return null
    return (
      <CloseWithToast
        message={
          query.isError ? 'Could not load this snippet.' : DELETED_MESSAGE
        }
        onClosed={closeSnippetEditor}
      />
    )
  }

  // A Careforce snippet cannot take a duty variant from here: the block's
  // action is Customize, which opens the shared text for the org's own copy.
  if (snippet.read_only) {
    const customizable = canCustomize(snippet)
    return (
      <LoadedSheet
        key={`${snippet.id}:${customizable ? 'customize' : 'view'}`}
        draft={
          customizable
            ? customizeDraftFor(snippet)
            : draftFor(snippet, { canShare, managedElsewhere })
        }
        onClosed={closeSnippetEditor}
      />
    )
  }
  if (dutyId === null) {
    return (
      <LoadedSheet
        key={snippet.id}
        draft={{
          ...draftFor(snippet, { canShare, managedElsewhere }),
          variant: null,
        }}
        onClosed={closeSnippetEditor}
      />
    )
  }
  return (
    <VariantSheet
      snippet={snippet}
      dutyId={dutyId}
      onClosed={closeSnippetEditor}
    />
  )
}

/**
 * Renders the snippet library sheet for edits requested from a script editor's
 * snippet block. Mounted once in the layout — see `useSnippetEditorStore` for
 * why the editor cannot own this itself.
 *
 * Split so the closed case subscribes to the store and NOTHING else. This is
 * mounted in `AuthLayout`, which every page renders, so pulling React Query and
 * Clerk in unconditionally would make them a hard dependency of the whole shell.
 */
export function SnippetEditorSheetHost() {
  const editingSnippetId = useSnippetEditorStore(
    (state) => state.editingSnippetId
  )
  const editingDutyId = useSnippetEditorStore((state) => state.editingDutyId)
  if (editingSnippetId === null) return null
  return <OpenEditor snippetId={editingSnippetId} dutyId={editingDutyId} />
}
